import Vue from 'vue';
import { Restaurant, DeliveryAddressValidationData, DeliveryApiException, DeliveryNotAvailableInThatCountryException, CustomerProfile, FacModalContent } from '@/models';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { CustomerDeliveryAddress } from '@/models';
import { AnalyticsEventCategory, AnalyticsEventAction, AnalyticsEventLabel } from '@/services/google/google-analytics.services';
import { CountryCode } from '@/store/ui';
import { FACOptions, countries } from '@/constants';
import result from 'lodash/result'
import { FindAChipotleClickEvent } from '@/services/analytics/events';
import { RestaurantSelectionType } from '@/services/analytics/platforms/adobe-analytics/models';
import { Routes } from '@/router';

const DEFAULT_FAVORITES_AMOUNT = 3;
const INVALID_PLACE_TYPES = [
  'administrative_area_level_1',
  'administrative_area_level_2',
  'administrative_area_level_3',
  'administrative_area_level_4',
  'administrative_area_level_5',
  'colloquial_area',
  'country',
  'intersection',
  'locality',
  'natural_feature',
  'neighborhood',
  'political',
  'point_of_interest',
  'post_box',
  'postal_code',
  'postal_code_prefix',
  'postal_code_suffix',
  'postal_town',
  'route',
  'sublocality',
  'sublocality_level_4',
  'sublocality_level_5',
  'sublocality_level_3',
  'sublocality_level_2',
  'sublocality_level_1',
];

@Component({
  name: 'CmgFindAChipotleSearch',
})
export default class CmgFindAChipotleSearch extends Vue {
  showMoreFavorites: boolean = false;
  isValidDeliveryAddress: boolean = false;
  canDeliverToSelectedCountry = true;
  validatingDelivery: boolean = false;
  isAwaitingAutoComplete: boolean = false;
  validationAddress: QueryAutocompletePrediction | null = null;
  showAutocomplete: boolean = false;
  nearbyScrolled: boolean = false;
  pickupScrolled: boolean = false;
  loadingRestaurant: Restaurant | null = null;
  chipotleSearchName = 'ChipotleSearch' + Math.random(); // spoil aggressive auto complete that interferes with our custom dropdown

  @Prop({ type: Object })
  map?: google.maps.Map;

  @Prop({ default: '', type: String })
  pickupSearchInput!: string;

  @Prop({ default: '', type: String })
  deliverySearchInput!: string;

  @Prop({ required: true, default: false, type: Boolean })
  showDeliveryAddressValidationResult!: boolean;

  @Prop({ required: true, default: true, type: Boolean })
  showPickup!: boolean;

  @Prop({ required: true, default: false, type: Boolean })
  showNoSuggestions!: boolean;

  @Prop({ type: Object })
  mostRecentRestaurant?: Restaurant;

  @Prop({ type: Object })
  selectedRestaurant?: Restaurant;

  @Prop({ default: () => [], type: Array })
  favoriteRestaurants?: Restaurant[];

  @Prop({ default: {}, type: Object })
  favoriteRestaurantIds?: { [key: number]: string };

  @Prop({ default: () => [], type: Array })
  nearbyRestaurants?: Restaurant[];

  @Prop({ default: () => [], type: Array })
  deliveryAddresses?: CustomerDeliveryAddress[];

  @Prop({ required: true, default: [], type: Array })
  autoCompleteSearchResults!: QueryAutocompletePrediction[];

  @Prop({ default: false, type: Boolean })
  hasEmptySearchResults!: boolean;

  @Prop({ type: Object })
  facOptions!: FACOptions;

  @Prop({ type: Object })
  facModalContent!: FacModalContent;

  selectedDeliveryAddress!: CustomerDeliveryAddress;
  arrowCounter: any = 0;
  isOpen: any;
  results: any;
  activeDescendant: any = '';
  private selectedAutoCompleteResult?: QueryAutocompletePrediction | null = null;

  async mounted() {
    this.$root.$on('select-restaurant-escape', (data: any) => {
      if (data) {
        this.$emit('select-restaurant', null);
      }
    });
    document.addEventListener('click', this.handleClickOutside);

    // Check if delivery ordering flag was turned off in APIM
    await this.$services.featureFlags.refreshApimFeatureFlags();
  }

  destroyed() {
    document.removeEventListener('click', this.handleClickOutside);
  }

  // Computed properties
  get viewableFavoritesCount(): number {
    return this.favoriteRestaurants && this.showMoreFavorites ? this.favoriteRestaurants.length : DEFAULT_FAVORITES_AMOUNT;
  }

  get defaultFavoritesAmount(): number {
    return DEFAULT_FAVORITES_AMOUNT;
  }

  get filteredFavoriteRestaurants(): Restaurant[] | undefined {
    if (!this.favoriteRestaurants) {
      return;
    }
    return this.favoriteRestaurants.slice(0, this.viewableFavoritesCount);
  }

  get hasRecentsFavorites(): boolean {
    return !!this.mostRecentRestaurant || (!!this.favoriteRestaurants && this.favoriteRestaurants.length > 0);
  }

  get targetIcon(): string {
    return this.$appStore.state.cms.images.icons.locationTarget.default;
  }

  get emptyHeartIcon(): string {
    return this.$appStore.state.cms.images.icons.heartEmpty.default;
  }

  get searchIconUrl(): string {
    return this.$appStore.state.cms.images.icons.search.default;
  }

  get dotsIconUrl(): string {
    return this.$appStore.state.cms.images.icons.dots.default;
  }

  get cmgLocationIconUrl(): string {
    return this.$appStore.state.cms.images.icons.chipotleLocation.default;
  }

  get deliveryIconUrl(): string {
    return this.$appStore.state.cms.images.icons.car.default;
  }

  get pepperSmallIconUrl(): string {
    return this.$appStore.state.cms.images.icons.pepperSmall.default;
  }

  get mapIconUrl(): string {
    return this.$appStore.state.cms.images.icons.map.default;
  }

  get closeButtonImageUrl(): string {
    return this.$appStore.state.cms.images.buttons.xDarkBrown.default;
  }

  get carUrl(): string {
    return this.$appStore.state.cms.images.icons.carGold.default;
  }

  get sadFaceUrl(): string {
    return this.$appStore.state.cms.images.icons.sadFace.default;
  }

  get isCanadian() {
    return this.$appStore.state.ui.countryProfile.countryCode == CountryCode.CA;
  }

  get customer(): CustomerProfile | null {
    const profile = this.$appStore.state.customer.profile;
    return profile && !profile.isGuest ? profile : null;
  }

  get profileCountryCode(): string {
    return this.customer ? this.customer.country || CountryCode.US : '';
  }

  get siteCountryCode(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get isDeliveryOnline(): boolean {
    return this.$services.utils.isDeliveryOnline();
  }

  get isAboveStorePaymentOnline(): boolean {
    const flags = this.$appStore.state.featureFlags;
    if (flags.orderingStatus && !flags.orderingStatus.aboveStorePaymentOnline) {
      return false;
    }

    return true;
  }

  get isUSPaymentOnline(): boolean {
    return this.isPayeezyPaymentOnline || this.isUComPaymentOnline;
  }

  get isCanadaPaymentOnline(): boolean {
    return this.isChasePaymentOnline;
  }

  get isPayeezyPaymentOnline(): boolean {
    const flags = this.$appStore.state.featureFlags;
    if (flags.orderingStatus && !flags.orderingStatus.isPayeezyPaymentOnline) {
      return false;
    }

    return true;
  }

  get isUKPaymentOnline(): boolean {
    return this.isChaseUKPaymentOnline;
  }

  get isChasePaymentOnline(): boolean {
    const flags = this.$appStore.state.featureFlags;
    if (flags.orderingStatus && !flags.orderingStatus.isChasePaymentOnline) {
      return false;
    }

    return true;
  }

  get isChaseUKPaymentOnline(): boolean {
    const flags = this.$appStore.state.featureFlags;
    if (flags.orderingStatus && !flags.orderingStatus.isChaseUKPaymentOnline) {
      return false;
    }

    return true;
  }

  get isUComPaymentOnline(): boolean {
    const flags = this.$appStore.state.featureFlags;
    if (flags.orderingStatus && !flags.orderingStatus.isUComPaymentOnline) {
      return false;
    }

    return true;
  }

  get showPickupDeliveryToggle(): boolean {
    const flags = this.$appStore.state.featureFlags;
    if (flags.orderingStatus) {
      return flags.orderingStatus.isDeliveryEnabled;
    }

    return this.facOptions.isDeliveryEnabled;
  }

  get searchInputPlaceholder() {
    return  this.showPickup ? this.facModalContent?.pickupSearchBar || 'City, State, or ZIP' : 'Address';
  }

  get headerText(): string {
    return this.facModalContent?.header || 'Find a Chipotle for Pickup';
  }

  get favoritesHeaderText(): string {
    return this.facModalContent?.favoriteHeader || 'Favorites';
  }

  get favoritesBodyText(): string {
    return this.facModalContent?.favoriteBody || 'Add any restaurant to your favorites for super fast ordering';
  }

  getRestaurantSelectionType(restaurant: Restaurant): RestaurantSelectionType {
    if (this.mostRecentRestaurant?.restaurantNumber === restaurant.restaurantNumber) {
      return RestaurantSelectionType.Recent;
    } else if (this.favoriteRestaurants?.find(r => r.restaurantNumber === restaurant.restaurantNumber)) {
      return RestaurantSelectionType.Favorite;
    } else {
      return RestaurantSelectionType.Nearby;
    }
  }

  setShowAutocomplete(newValue: boolean) {
    setTimeout(() => (this.showAutocomplete = newValue), 100);
  }

  getCityFromSearchResult(searchResult: QueryAutocompletePrediction): string {
    const index = searchResult.description.indexOf(',');
    if (index > 0) {
      return searchResult.description.substr(0, index);
    }

    return searchResult.description;
  }

  getAddressLine2FromSearchResult(searchResult: QueryAutocompletePrediction): string {
    const index = searchResult.description.indexOf(',');
    if (index > 2) {
      return searchResult.description.substring(index + 2);
    }

    return '';
  }

  onClickSearchResult(searchResult: QueryAutocompletePrediction) {
    this.selectedAutoCompleteResult = searchResult;
    this.onSearchSubmit();
  }

  onTogglePickupDelivery(searchState: 'pickup' | 'delivery') {
    ((this.$refs.csearch as Vue).$el.querySelector('.input') as HTMLInputElement).focus();

    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.Behavior,
      action: AnalyticsEventAction.Toggle,
      label: searchState == 'pickup'
        ? AnalyticsEventLabel.RestaurantSearchPickup : AnalyticsEventLabel.RestaurantSearchDelivery,
    });

    this.$emit('toggle-pickup-delivery', searchState);
    this.selectedAutoCompleteResult = null;
  }

  toggleShowMoreFavorites(): void {
    this.showMoreFavorites = !this.showMoreFavorites;
  }

  findNearby() {
    this.$services.analytics.send(
      new FindAChipotleClickEvent('searchInitiated', undefined, 'Nearby Search', this.selectedAutoCompleteResult?.description),
    );
    this.$emit('find-nearby');
  }

  onSearchInput(value: string) {
    this.$emit('search-input', value);
  }

  onSearchSubmit() {
    if (this.selectedAutoCompleteResult == null && this.autoCompleteSearchResults && this.autoCompleteSearchResults.length > 0) {
      this.selectedAutoCompleteResult = this.autoCompleteSearchResults[0];
    }

    if (!this.showPickup) {
      // if we're in the delivery search view
      if (this.selectedAutoCompleteResult) {
        this.submitDeliveryAddress(this.selectedAutoCompleteResult);
        this.onSearchInput(this.selectedAutoCompleteResult.description);
        this.$services.analytics.send(new FindAChipotleClickEvent('deliveryAddressLookup', undefined, 'Delivery Address Search', this.selectedAutoCompleteResult.description));
        this.selectedAutoCompleteResult = null;
      } else {
        this.isAwaitingAutoComplete = true;
        return;
      }
    } else {
      let searchvalue = this.pickupSearchInput;
      if (this.selectedAutoCompleteResult) {
        searchvalue = this.selectedAutoCompleteResult.description;
        this.onSearchInput(searchvalue);
      }

      this.$services.analytics.send(new FindAChipotleClickEvent('searchInitiated', undefined, 'Restaurant Search', this.selectedAutoCompleteResult?.description));

      this.$emit('search-submit', searchvalue);
    }

    this.selectedAutoCompleteResult = null;
    Vue.nextTick(() => {
      setTimeout(() => {
        const focusElement = document.getElementsByClassName('empty-text')[0] as HTMLElement;
        const focusNearby = document.getElementsByClassName('nearby-text')[0] as HTMLElement;
        if (focusElement) {
          focusElement.focus();
        }
        if (focusNearby) {
          focusNearby.focus();
        }
      }, 3000);
    });
  }

  onClearSearch() {
    this.$emit('search-clear');
    ((this.$refs.csearch as Vue).$el.querySelector('.input') as HTMLInputElement).focus();
    this.nearbyScrolled = false;
  }

  @Watch('autoCompleteSearchResults', { immediate: false, deep: true })
  onAutocompleteSearchResultsChange(autoCompleteSearchResults: QueryAutocompletePrediction[]) {
    this.arrowCounter = -1;
    if (!this.isAwaitingAutoComplete) {
      return;
    }
    this.selectedAutoCompleteResult = this.autoCompleteSearchResults[0];
    this.onSearchSubmit();
    this.isAwaitingAutoComplete = false;
    this.onSearchInput('');
  }

  async submitDeliveryAddress(place: QueryAutocompletePrediction) {
    this.validatingDelivery = true;
    this.validationAddress = place;
    this.canDeliverToSelectedCountry = true;

    const isValidAddressType = place.types
      ? place.types.every(type => {
          return INVALID_PLACE_TYPES.indexOf(type) === -1;
        })
      : false;

    if (!isValidAddressType) {
      this.validatingDelivery = false;
      this.isValidDeliveryAddress = false;
      this.$emit('show-delivery-address-validation-result', true);
      return;
    }
    await this.validateDeliveryAddress(place).catch(e => {
      if (e instanceof DeliveryNotAvailableInThatCountryException) {
        this.canDeliverToSelectedCountry = false;
      }
      this.$services.googleAnalytics.sendCustomEvent({
        category: AnalyticsEventCategory.Delivery,
        action: AnalyticsEventAction.ErrorMessage,
        label: AnalyticsEventLabel.AddressCantDeliver,
      });
    });

    this.validatingDelivery = false;
    this.$emit('show-delivery-address-validation-result', true);
  }

  onFocus() {
    this.$nextTick(() => this.$emit('focus'));
  }

  onArrowDown() {
    if (this.arrowCounter < this.autoCompleteSearchResults.length) {
      this.arrowCounter = this.arrowCounter + 1;
      this.setActiveDescendent();
    }
  }

  onArrowUp() {
    if (this.arrowCounter > 0) {
      this.arrowCounter = this.arrowCounter - 1;
      this.setActiveDescendent();
    }
  }

  onSearchEnter(event: any) {
    if (this.arrowCounter > 0) {
      this.selectedAutoCompleteResult = this.autoCompleteSearchResults[this.arrowCounter];
      event.target.value = this.autoCompleteSearchResults[this.arrowCounter].description;
      event.target.value = result;
      this.onSearchInput(event.target.value);
      this.$emit('search-submit', event.target.value);
      this.arrowCounter = -1;
    }
  }

  handleClickOutside(evt: any) {
    if (!this.$el.contains(evt.target)) {
      this.arrowCounter = -1;
    }
  }

  setActiveDescendent() {
    this.activeDescendant = this.getId(this.arrowCounter);
  }

  getId(index: any) {
    return `result-item-${index}`;
  }

  isSelected(index: any) {
    return index === this.arrowCounter;
  }

  onRestaurantSelected(restaurant: Restaurant) {
    if (this.customer) {
      let country = countries.us;
      switch (restaurant.address.countryCode) {
        case CountryCode.US:
          country = countries.us;
          break;
        case CountryCode.CA:
          country = countries.ca;
          break;
        case CountryCode.GB:
          country = countries.gb;
          break;
      }
      this.$services.ui.setCountryProfile(country, false);
    }

    this.$services.analytics.send(
      new FindAChipotleClickEvent(
        'locationSelected',
        restaurant.restaurantNumber,
        this.getRestaurantSelectionType(restaurant),
        this.selectedAutoCompleteResult?.description,
      ),
    );

    this.$emit('select-restaurant', restaurant);
    this.$services.ui.firstFocus();
  }

  onRestaurantMouseover(restaurant: Restaurant) {
    this.$emit('mouseover-restaurant', restaurant);
  }

  onRestaurantMouseleave(restaurant: Restaurant) {
    this.$emit('mouseleave-restaurant', restaurant);
  }

  onCloseRestaurantDetails() {
    this.$emit('select-restaurant', null);
  }

  onSetLoading(isLoading: boolean) {
    this.validatingDelivery = isLoading;
  }

  onShowDeliveryAddressValidationResult(showDeliveryAddressValidationResult: boolean) {
    this.$emit('show-delivery-address-validation-result', showDeliveryAddressValidationResult);
  }

  async onOrderFromRestaurant(restaurant: Restaurant) {
    const args = this.$appStore.state.ui.modals.findAChipotle.args;
    this.loadingRestaurant = restaurant;

    try {
      await this.$services.restaurant.selectRestaurant(restaurant, true);

      // If restaurant changes, experience request will fire on meal-builder page,
      // so no need to fire the experience request when we are on meal-builder page.
      if (this.$route.name != Routes.MealBuilder) {
        this.$services.adobeTarget.sendAdobeTargetExperienceRequest(this.$route.name);
      }

      if (this.$services.order.isDelivery()) {
        await this.$services.order.setDeliveryAddress(null);
      }

      // If restaurant changes, need to set the checkout provider
      try {
        const providers = await this.$services.transaction.getProviders(restaurant.restaurantNumber.toString());
        if (providers && providers.length > 0 && providers[0]) {
          const providerId = providers[0].paymentProviderId;
          this.$services.checkout.setPaymentProvider(providerId);
        }
      } catch (error: any) {
        this.$services.logger.error('getProvider: Failed to get provider when changing pickup restaurant', error);
      }

      this.$services.googleAnalytics.sendCustomEvent({
        category: AnalyticsEventCategory.Behavior,
        action: AnalyticsEventAction.Click,
        label: AnalyticsEventLabel.RestaurantSearchPickupCommit,
      });
      this.$services.analytics.send(new FindAChipotleClickEvent('pickupLocation'));
      this.$services.analytics.setIfCustomerSetLocation(true);
    } catch (e: any) {
      this.$services.logger.error('Something went wrong selecting restaurant', undefined, e);
      return;
    } finally {
      //set router to home after a restaurant has been selected ONLY if we are directly on /find-a-chipotle page
      if(location.pathname === '/find-a-chipotle'){
        this.$router.replace({ name: Routes.Home, hash: '#menu' });
      }
      this.loadingRestaurant = null;
    }

    if (args && args.onClose) {
      args.onClose();
    }

    this.$services.ui.setModalVisibility('findAChipotle', false);

    this.$services.ui.setModalVisibility('toast', !args?.suppressPickupDeliveryToast, {
      title: 'PICKING UP FROM',
      subTitle: (this.$services.customer.getFavoriteRestaurantName(restaurant.restaurantNumber) || '').toUpperCase(),
      body: this.$services.customer.getFavoriteRestaurantName(restaurant.restaurantNumber)
        ? restaurant.address.addressLine1!
        : `${restaurant.address.addressLine1}, ${restaurant.address.locality}, ${restaurant.address.administrativeArea} ${(restaurant.address.postalCode || '').split('-')[0]
        }`,
      icon: this.$appStore.state.cms.images.logos.pepperWhite.default,
      after: async () => {
        const epops = await this.$services.epop.getEpopsForRestaurant(restaurant.restaurantNumber);
        if (epops && epops.length > 0) {
          this.$services.ui.setModalVisibility('epop', true, {
            epops,
          });
          this.$services.ui.firstFocus();
        }
      },
    });

    Vue.nextTick(() => {
      setTimeout(() => {
        const focusNearby = document.getElementsByClassName('customer-greeting')[0] as HTMLElement;
        const focusNearbysignin = document.getElementsByClassName('sign-in')[0] as HTMLElement;
        if (focusNearby) {
          focusNearby.focus();
        }
        if (focusNearbysignin) {
          focusNearbysignin.focus();
        }
      }, 6000);
    });
  }

  closeValidationResultView() {
    this.$emit('search-clear');
    ((this.$refs.csearch as Vue).$el.querySelector('.input') as HTMLInputElement).focus();
    this.validationAddress = null;
    this.$emit('show-delivery-address-validation-result', false);
  }

  showPickupView() {
    this.$emit('toggle-pickup-delivery', 'pickup');
    ((this.$refs.csearch as Vue).$el.querySelector('.input') as HTMLInputElement).focus();
    this.closeValidationResultView();
  }

  async validateDeliveryAddress(place: QueryAutocompletePrediction): Promise<DeliveryAddressValidationData | undefined> {
    if (!this.map) {
      this.$services.logger.debug('Map not ready yet!');
      return;
    }

    const details: google.maps.places.PlaceResult = await this.$services.googleMaps.getPlaceDetails(place.place_id, this.map);

    if (!details || !details.address_components) {
      throw new Error('Unable to get place details');
    }

    const deliveryAddress = this.$services.findAChipotle.createDeliveryAddressValidationRequest(details);

    const countryProfile = deliveryAddress.dropOffAddress.countryCode == CountryCode.CA ? countries.ca : countries.us;
    this.$services.ui.setCountryProfile(countryProfile, false);

    const validationResult = await this.$services.order.validateDeliveryAddress(deliveryAddress!);

    if (validationResult) {
      const selectedDeliveryAddress = this.$services.findAChipotle.setSelectedDeliveryAddress(details);
      this.isValidDeliveryAddress = !(validationResult instanceof DeliveryApiException) && validationResult.status === 200 ? true : false;
      this.selectedDeliveryAddress = selectedDeliveryAddress;

      if (selectedDeliveryAddress) {
        this.$emit('select-delivery-address', selectedDeliveryAddress);
      }
    }
  }

  onResturantListScroll({ target: { scrollTop, className } }: { target: { scrollTop: number; className: string } }) {
    if (className === 'nearby-list') {
      if (scrollTop === 0) {
        this.nearbyScrolled = false;
      } else {
        this.nearbyScrolled = true;
      }
    }

    if (className === 'pickup-view') {
      if (scrollTop === 0) {
        this.pickupScrolled = false;
      } else {
        this.pickupScrolled = true;
      }
    }
  }

  async onSelectSavedAddress(address: CustomerDeliveryAddress) {
    this.selectedDeliveryAddress = address;
    this.$services.ui.setCountryProfile(address.countryCode === CountryCode.CA ? countries.ca : countries.us, false);

    const validationResult = await this.$services.order.validateDeliveryAddress(this.$services.findAChipotle.customerDeliveryAddressToValidationRequest(address));

    this.isValidDeliveryAddress = !(validationResult instanceof DeliveryApiException) && validationResult.status === 200 ? true : false
    this.canDeliverToSelectedCountry = true;

    this.validationAddress = {
      types: [],
      description: '',
      matched_substrings: [],
      place_id: '',
      terms: [],
      structured_formatting: {
        main_text: `${address.streetAddress1}${address.streetAddress2 ? ', ' + address.streetAddress2 : ''}`,
        secondary_text: `${address.city}, ${address.state}, ${address.countryCode || 'USA'}`,
      },
    };

    this.$emit('show-delivery-address-validation-result', true);
    this.$nextTick(() => this.$emit('focus'));
    this.$emit('select-delivery-address', address);
  }

  onDotsDrag(event: any) {
    this.$emit('dots-drag-event', event);
  }
}
